// Maps settings -> CSS variables / data-attributes on <html>. Universal theme.
import { getAll } from "../shared/settings-store.js";
import { FONTS, loadFont, loadDisplayFont } from "../shared/fonts.js";
import { contrast, inkFor, onAccent } from "../shared/color.js";
import { THEMES } from "../shared/settings-schema.js";
import { snowOpen } from "../shared/seasons.js";
import { perfActive } from "./perf.js";

function shade(hex, amt) {
	try {
		const c = parseInt(hex.slice(1), 16);
		let r = (c >> 16) & 255, g = (c >> 8) & 255, b = c & 255;
		r = Math.round(r * amt); g = Math.round(g * amt); b = Math.round(b * amt);
		return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
	} catch (e) { return hex; }
}

// Corner-roundness tiers. Each control in the CSS uses --radius (general),
// --radius-sm (small controls), --radius-lg (panels), or --radius-pill (the
// search bar), so one setting reshapes the whole UI. "rounded" matches the
// historical hardcoded values so the default look is unchanged.
const RADII = {
	sharp: { sm: "2px", base: "4px", lg: "6px", pill: "10px" },
	rounded: { sm: "6px", base: "8px", lg: "12px", pill: "22px" },
	soft: { sm: "10px", base: "14px", lg: "18px", pill: "30px" },
};

// The accent works as a fill on any look, but as text or a focus ring it has to clear 4.5:1
// on the page and on raised panels, so derive a readable shade from the theme's own tokens.
// The tokens are read from the stylesheet rules rather than getComputedStyle, which would
// force a style pass in the middle of boot.
const TOKENS = ["bg", "surface", "surface-2", "on-accent", "accent-ink"];
function sheetTokens(theme) {
	const want = [":root", ':root[data-theme="' + theme + '"]'];
	const out = {};
	for (const sheet of document.styleSheets) {
		let rules = null;
		try { rules = sheet.cssRules; } catch { continue; }
		for (const rule of rules || []) {
			if (!rule.style || !want.includes(rule.selectorText)) continue;
			for (const n of TOKENS) { const v = rule.style.getPropertyValue("--" + n).trim(); if (v) out[n] = v; }
		}
	}
	return out;
}
function applyAccentInk(root, theme, accent) {
	try {
		const t = sheetTokens(theme);
		if (!t.bg) return;
		const own = t["on-accent"];
		const dark = own && own.length === 7 && contrast(own, "#ffffff") > 10 ? own : "#1a1500";
		// Only touch :root when a value really changes: any custom property set there restyles
		// the whole page, and on the default look both values already match the CSS.
		const set = (name, v, css) => {
			const cur = (root.style.getPropertyValue(name) || css || "").trim().toLowerCase();
			if (cur !== v) root.style.setProperty(name, v);
		};
		set("--accent-ink", inkFor(accent, [t.bg, t.surface || t.bg, t["surface-2"] || t.bg]), /^var\(/.test(t["accent-ink"] || "") ? accent : t["accent-ink"]);
		set("--on-accent", onAccent(accent, dark), own);
	} catch { /* tokens unreadable: the CSS defaults stay */ }
}

// Waits for an idle moment after boot (the CSS defaults cover until then) and only reruns when
// the theme or accent actually changed.
let inkKey = "", inkJob = 0;
const idle = typeof requestIdleCallback === "function" ? (fn) => requestIdleCallback(fn, { timeout: 1500 }) : (fn) => setTimeout(fn, 200);
const unidle = typeof cancelIdleCallback === "function" ? cancelIdleCallback : clearTimeout;
function scheduleAccentInk(root, theme, accent) {
	const key = theme + "|" + accent;
	if (key === inkKey) return;
	inkKey = key;
	unidle(inkJob);
	inkJob = idle(() => applyAccentInk(root, theme, accent));
}

export function applyTheme() {
	const s = getAll();
	const root = document.documentElement;
	root.style.setProperty("--accent", s.accent);
	root.style.setProperty("--accent-2", shade(s.accent, 0.78));
	const font = FONTS[s.font] || FONTS.verdana;
	root.style.setProperty("--font", font.stack);
	loadFont(s.font); // lazily fetch the web font if this choice needs one

	// Resolve the base scheme to a theme id + light/dark mode. "auto" follows the OS.
	let id = s.theme, mode;
	// "auto" follows the OS, and an OS asking for more contrast gets High Contrast.
	if (id === "auto") { mode = matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark"; id = matchMedia("(prefers-contrast: more)").matches ? "contrast" : mode; if (id === "contrast") mode = "dark"; }
	else { const t = THEMES[id]; if (!t || id === "auto") { id = "dark"; mode = "dark"; } else mode = t.mode; }
	root.dataset.theme = id;
	root.dataset.mode = mode;
	loadDisplayFont(id);
	scheduleAccentInk(root, id, s.accent);

	const r = RADII[s.cornerStyle] || RADII.rounded;
	root.style.setProperty("--radius-sm", r.sm);
	root.style.setProperty("--radius", r.base);
	root.style.setProperty("--radius-lg", r.lg);
	root.style.setProperty("--radius-pill", r.pill);

	root.dataset.density = s.density === "compact" ? "compact" : "comfortable";
	root.dataset.animations = s.animations ? "on" : "off";
	// Lite mode for low-end devices - flattens effects + caps live tabs (see perf.js
	// and the [data-perf] rules in theme.css). Re-resolved here so a live settings
	// change (or an "auto" device match) takes effect immediately.
	root.dataset.perf = perfActive(s.performanceMode) ? "on" : "off";
	root.dataset.brand = s.showBrand === false ? "off" : "on";
	root.dataset.status = s.showStatus === false ? "off" : "on";
	// Light snowfall: only in its season, and not for someone who paused moving wallpapers. Lite
	// mode, Animations off and reduced motion turn it off in theme.css.
	root.dataset.snow = s.snowfall === true && s.wallpaperAnimate !== false && snowOpen() ? "on" : "off";
}
